import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../utils/constants';
import { fetchWithAbort } from '../utils/fetchWithAbort';

export function useTopHolders() {
  const [data, setData] = useState({
    holders: [],
    totalHoldings: 0,
    totalHolders: 0,
    top10Percent: 0,
    byType: { sv: { count: 0, balance: 0 }, validator: { count: 0, balance: 0 }, app: { count: 0, balance: 0 } },
    asOfTime: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchHolders = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    const result = await fetchWithAbort(
      `${API_BASE}/top-holders`,
      { cacheTTL: 60000 }, // Cache for 60 seconds (longer since data updates less frequently)
      signal
    );

    // Check if aborted or unmounted
    if (signal.aborted || !isMountedRef.current) {
      return;
    }

    if (result.ok) {
      setData(result.data);
    } else if (!result.aborted) {
      setError(result.error || 'Failed to fetch top holders');
      console.error('Error fetching top holders:', result.error);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchHolders();
    // Refresh every 5 minutes (matches cache TTL)
    const interval = setInterval(fetchHolders, 300000);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchHolders]);

  return { ...data, loading, error, refetch: fetchHolders };
}
